import { useState, type KeyboardEvent } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  HISTORY_MAX_RANGE_DAYS,
  compareDates,
  daysBetween,
  formatCalendarDate,
  type CalendarDate,
} from '@findmy/shared';
import { rangeLabel, type DayRange } from '../lib/history';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

interface Month {
  year: number;
  month: number;
}

const monthOf = (date: CalendarDate): Month => ({ year: date.year, month: date.month });
const shiftMonth = ({ year, month }: Month, by: number): Month => {
  const index = year * 12 + (month - 1) + by;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
};
const monthIndex = (m: Month) => m.year * 12 + m.month;

function daysIn({ year, month }: Month): CalendarDate[] {
  const count = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) => ({ year, month, day: i + 1 }));
}

/** Monday-first column of the 1st of the month. */
const leadingBlanks = ({ year, month }: Month) =>
  (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;

/**
 * A button showing the range ("17/10/2026 – 24/10/2026") that opens a month calendar.
 * Pick the first day, then the last; picking the same day twice chooses just that day.
 * Ranges are at most 31 days, and can't start before `earliest` or end after `today`.
 * (The browser's own date input isn't used: it shows dates in the browser's format,
 * which may not be DD/MM/YYYY.)
 */
export function DateRangePicker({
  value,
  onChange,
  today,
  earliest,
}: {
  value: DayRange;
  onChange: (range: DayRange) => void;
  today: CalendarDate;
  earliest: CalendarDate;
}) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState<Month>(monthOf(value.to));
  const [start, setStart] = useState<CalendarDate | null>(null);

  const toggle = () => {
    setShown(monthOf(value.to));
    setStart(null);
    setOpen(!open);
  };

  const pick = (day: CalendarDate) => {
    if (!start) {
      setStart(day);
      return;
    }
    const [from, to] = compareDates(day, start) < 0 ? [day, start] : [start, day];
    onChange({ from, to });
    setStart(null);
    setOpen(false);
  };

  const disabled = (day: CalendarDate) =>
    compareDates(day, today) > 0 ||
    compareDates(day, earliest) < 0 ||
    (start !== null && Math.abs(daysBetween(start, day)) >= HISTORY_MAX_RANGE_DAYS);

  const highlighted = (day: CalendarDate) =>
    start
      ? compareDates(day, start) === 0
      : compareDates(day, value.from) >= 0 && compareDates(day, value.to) <= 0;

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={`Dates: ${rangeLabel(value)}`}
        className="inline-flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800"
      >
        <CalendarDays className="h-4 w-4 text-slate-500" aria-hidden />
        {rangeLabel(value)}
      </button>

      {open && (
        <div
          role="group"
          aria-label="Choose dates"
          onKeyDown={onKeyDown}
          className="mt-2 rounded-xl p-3 ring-1 ring-slate-200 dark:ring-slate-700"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShown(shiftMonth(shown, -1))}
              disabled={monthIndex(shown) <= monthIndex(monthOf(earliest))}
              aria-label="Previous month"
              className="rounded-lg p-1 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium" aria-live="polite">
              {monthFormatter.format(Date.UTC(shown.year, shown.month - 1, 1))}
            </span>
            <button
              type="button"
              onClick={() => setShown(shiftMonth(shown, 1))}
              disabled={monthIndex(shown) >= monthIndex(monthOf(today))}
              aria-label="Next month"
              className="rounded-lg p-1 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 text-center text-xs text-slate-500" aria-hidden>
            {WEEKDAYS.map((d) => (
              <span key={d} className="py-1">
                {d}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-0.5 text-center text-sm">
            {Array.from({ length: leadingBlanks(shown) }, (_, i) => (
              <span key={`blank-${i}`} />
            ))}
            {daysIn(shown).map((day) => {
              const on = highlighted(day);
              const isToday = compareDates(day, today) === 0;
              return (
                <button
                  key={day.day}
                  type="button"
                  onClick={() => pick(day)}
                  disabled={disabled(day)}
                  aria-label={formatCalendarDate(day)}
                  aria-pressed={on}
                  className={`mx-auto h-8 w-8 rounded-full disabled:cursor-not-allowed disabled:opacity-30 ${
                    on ? 'bg-blue-600 font-semibold text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                  } ${isToday && !on ? 'font-semibold text-blue-700 dark:text-sky-400' : ''}`}
                >
                  {day.day}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {start
              ? `From ${formatCalendarDate(start)}: now choose the last day (up to ${HISTORY_MAX_RANGE_DAYS} days).`
              : 'Choose the first day.'}
          </p>
        </div>
      )}
    </div>
  );
}
